import { useState } from "react";
import { MapPin, Navigation, Route, Loader2, Clock, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";

export const LocationMap = ({ property }) => {
  const address = property?.address || property?.location;
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!address) return null;
  const q = encodeURIComponent(address);
  const embedSrc = `https://www.google.com/maps?q=${q}&z=14&output=embed`;
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${q}`;

  const calc = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/distance", { query });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="location" className="py-20 md:py-28 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <p className="text-primary font-medium text-sm uppercase tracking-widest mb-2">Where you'll be</p>
          <h2 className="font-script text-6xl md:text-8xl leading-tight text-foreground">
            Find your way here
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-sm border border-border h-[320px] md:h-[440px]">
            <iframe
              title="Property location map"
              data-testid="location-map"
              src={embedSrc}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>

          <div className="flex flex-col gap-6">
            <div className="bg-card rounded-2xl p-7 shadow-sm">
              <span className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <MapPin className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </span>
              <h3 className="font-serif text-2xl font-semibold text-foreground mb-2">Our address</h3>
              <p className="text-muted-foreground leading-relaxed mb-6">{address}</p>
              <a
                href={directions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <Navigation className="h-4 w-4" strokeWidth={1.75} /> Get directions
              </a>
            </div>

            <div className="bg-card rounded-2xl p-7 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Route className="h-5 w-5 text-secondary" strokeWidth={1.75} />
                <h3 className="font-serif text-xl font-semibold text-foreground">How far is it?</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Type any place or address to see the driving distance and time from the home.</p>
              <form onSubmit={calc} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g. Phoenix Airport"
                    className="pl-9 rounded-lg"
                    data-testid="distance-input"
                  />
                </div>
                <Button type="submit" disabled={loading || !query.trim()} className="rounded-lg bg-secondary hover:bg-secondary/90" data-testid="distance-calc-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
                </Button>
              </form>

              {error && <p className="text-sm text-destructive mt-3" data-testid="distance-error">{error}</p>}

              {result && (
                <div className="mt-4 rounded-xl bg-accent/60 p-4" data-testid="distance-result">
                  <p className="text-sm text-muted-foreground mb-2 truncate">to {result.destination}</p>
                  <div className="flex items-center gap-5">
                    <div className="flex items-center gap-2">
                      <Route className="h-5 w-5 text-primary" strokeWidth={1.75} />
                      <span className="font-serif text-2xl font-semibold text-foreground">{result.miles} mi</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-primary" strokeWidth={1.75} />
                      <span className="font-serif text-2xl font-semibold text-foreground">{result.duration_text}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
